import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css";
import { Calendar } from "react-date-range";
import { addDays } from "date-fns";
import { format } from "date-fns";
import { joinClassNames } from "../../utils/joinClassNames";
import { bemClassName } from "../../utils/bemClassName";
import styles from "./Facets.module.css";
import { SearchFieldRange } from "./SearchForm";
import { useField, Field } from "formik";
import { useEffect, useRef, useState, ComponentProps } from "react";
import { useClickAway } from "../../hooks/useClickAway";
import { useMediaQuery } from "react-responsive";
import { MEDIA_QUERIES } from "../../utils/namedConstants";
import { useSearchResults } from "../../context/DataContext";

const getUseFieldProps = (name: string) => {
    const [field, meta, helpers] = useField(name);
    return { field, meta, helpers };
};

const CalendarIcon = (props: ComponentProps<"svg">) => {
    return (
        <svg
            width="16"
            height="17"
            viewBox="0 0 16 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            {...props}
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.77777 14.7232H14.2222V4.94542H1.77777V14.7232ZM15.1111 2.27875H13.3333V1.39875C13.3333 0.902754 12.936 0.500977 12.4444 0.500977C11.9529 0.500977 11.5556 0.902754 11.5556 1.39875V2.27875H4.44444V1.39875C4.44444 0.902754 4.04711 0.500977 3.55556 0.500977C3.064 0.500977 2.66667 0.902754 2.66667 1.39875V2.27875H0.888889C0.397333 2.27875 0 2.67609 0 3.16764V15.6121C0 16.1028 0.397333 16.501 0.888889 16.501H15.1111C15.6027 16.501 16 16.1028 16 15.6121V3.16764C16 2.67609 15.6027 2.27875 15.1111 2.27875ZM8.8889 12.9641H12.4445V9.40853H8.8889V12.9641Z"
                fill="#017D7D"
            />
        </svg>
    );
};

const DateRangeField = ({
    label,
    nameStart,
    nameEnd,
}: Required<SearchFieldRange>) => {
    const { config } = useSearchResults();
    const [startDateOpen, setStartDateOpen] = useState(false);
    const [endDateOpen, setEndDateOpen] = useState(false);
    const start = getUseFieldProps(nameStart);
    const end = getUseFieldProps(nameEnd);
    const startInputRef = useRef<HTMLInputElement>(null);
    const endInputRef = useRef<HTMLInputElement>(null);

    // Close date picker on click away
    const ref = useClickAway(() => {
        setStartDateOpen(false);
        setEndDateOpen(false);
    });

    const isMobileScreen = !useMediaQuery({
        query: MEDIA_QUERIES.SMALL,
    });

    const { value: startValue } = start.meta;
    const { value: endValue } = end.meta;
    const dateFormat = "yyyy-LL-dd";
    const startDate = start.meta.value ? start.meta.value : new Date();
    const endDate = end.meta.value ? end.meta.value : new Date();

    /**
     * Handler callback function for the DateRange component
     * Formats start and end dates and set's them in state
     *
     * @param item Range item passed from the onChange Event
     */
    const handleStartDateChange = (date: Date) => {
        if (date) {
            const startDate = format(date, dateFormat);
            start.helpers.setValue(startDate);
            setStartDateOpen(false);
        }
    };
    const handleEndDateChange = (date: Date) => {
        if (date) {
            const endDate = format(date, dateFormat);
            end.helpers.setValue(endDate);
            setEndDateOpen(false);
        }
    };

    /**
     * Handler callback for input date field click
     * Sets range state to open and prevents default datepicker if not on mobile
     *
     * @param event Mouse event
     */
    const onStartInputClick = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        inputRef: React.RefObject<HTMLInputElement>
    ) => {
        event.preventDefault();
        // use browser calendar picker on mobile
        if (isMobileScreen && inputRef.current) {
            inputRef.current.showPicker();
        }
        if (inputRef.current) {
            inputRef.current.focus();
        }
        setEndDateOpen(false);
        setStartDateOpen(true);
    };
    const onEndInputClick = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        inputRef: React.RefObject<HTMLInputElement>
    ) => {
        event.preventDefault();
        // use browser calendar picker on mobile
        if (isMobileScreen && inputRef.current) {
            inputRef.current.showPicker();
        }
        if (inputRef.current) {
            inputRef.current.focus();
        }
        setStartDateOpen(false);
        setEndDateOpen(true);
    };

    // keyboard accessibility
    const onStartInputKeyPress = (
        event: React.KeyboardEvent<HTMLDivElement>,
        inputRef: React.RefObject<HTMLInputElement>
    ) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (inputRef.current) {
                inputRef.current.focus();
            }
            setStartDateOpen(true);
        }
    };
    const onEndInputKeyPress = (
        event: React.KeyboardEvent<HTMLDivElement>,
        inputRef: React.RefObject<HTMLInputElement>
    ) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (inputRef.current) {
                inputRef.current.focus();
            }
            setEndDateOpen(true);
        }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
            setStartDateOpen(false);
            setEndDateOpen(false);
        }
    };

    useEffect(() => {
        if (startDateOpen || endDateOpen) {
            document.addEventListener("keydown", handleKeyDown);
        } else {
            document.removeEventListener("keydown", handleKeyDown);
        }

        // Remove event listener on component unmount
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [startDateOpen, endDateOpen]);

    let columnClassName = `col-xs-12 col-md-6 col-lg-4`;
    if (
        [
            "delwp-search-listings-ees-push-stage",
            "delwp-search-listings-ees-push",
        ].includes(config.collection)
    ) {
        if (label === "Date range (Referral accepted)") {
            columnClassName = `col-xs-12 col-md-6 referral`;
        }
    }

    return (
        <div className={columnClassName}>
            <label
                className={joinClassNames([
                    bemClassName("find-a-panel", "search-title"),
                    styles.checkboxGroupLabel,
                ])}
            >
                {label.trim()}
            </label>
            <div ref={ref}>
                <div className={styles.dateRangeInputWrapper}>
                    <div
                        tabIndex={0}
                        className={styles.dateInputField}
                        onClick={(e) => onStartInputClick(e, startInputRef)}
                        onKeyDown={(e) =>
                            onStartInputKeyPress(e, startInputRef)
                        }
                    >
                        <CalendarIcon />
                        <Field
                            type="date"
                            {...start.field}
                            value={startValue || ""}
                            innerRef={startInputRef}
                            className={"hidden"}
                        />
                        <span className={styles.placeholder}>
                            {startValue || "Enter date from"}
                        </span>
                        {startDateOpen && !isMobileScreen && (
                            <div className={styles.dateRangeFieldWrapper}>
                                <Calendar
                                    onChange={handleStartDateChange}
                                    date={new Date(startDate)}
                                    minDate={new Date(2005, 0, 1)}
                                    maxDate={addDays(new Date(), 365)}
                                />
                            </div>
                        )}
                    </div>

                    <div
                        tabIndex={0}
                        className={styles.dateInputField}
                        onClick={(e) => onEndInputClick(e, endInputRef)}
                        onKeyDown={(e) => onEndInputKeyPress(e, endInputRef)}
                    >
                        <CalendarIcon />
                        <Field
                            type="date"
                            {...end.field}
                            value={endValue || ""}
                            innerRef={endInputRef}
                            className={"hidden"}
                        />
                        <span className={styles.placeholder}>
                            {endValue || "Enter date to"}
                        </span>
                        {endDateOpen && !isMobileScreen && (
                            <div className={styles.dateRangeFieldWrapper}>
                                <Calendar
                                    onChange={handleEndDateChange}
                                    date={new Date(endDate)}
                                    minDate={new Date(2005, 0, 1)}
                                    maxDate={addDays(new Date(), 365)}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DateRangeField;
